/* eslint-disable no-nested-ternary */
import {
  COUNTRY_CODES,
  WAREHOUSE_GET,
  WAREHOUSE_SEARCH_GET,
  WAREHOUSE_UPDATE,
} from '@constants';
import { useAuth } from '@context';
import {
  ACCESS_SPACE_UOM,
  CAPACITY_UOM,
  CURRENCY,
  SERVICE_TIME_UNITS,
  SQUARE_ACCESS_SPACE_UOM,
  STANDARD_BUSINESS_HOURS,
} from '@forms/WarehouseForm/constants';
import {
  WarehouseFormValues,
  WarehouseImage,
} from '@forms/WarehouseForm/types';
import { WarehouseForm } from '@forms/WarehouseForm/WarehouseForm';
import { useAuthHttp } from '@hooks';
import { UserRoleService } from '@services';
import {
  Warehouse,
  WarehouseData,
} from '@views/WarehouseDetails/WarehouseDetails';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { useAtomValue } from 'jotai';
import { businessProfileAtom } from '../../store/jotai';

export function UpdateWarehouse() {
  useDocumentTitle('FLOX - Update Warehouse');
  const { user } = useAuth();
  const authHttp = useAuthHttp();
  const businessProfile = useAtomValue(businessProfileAtom);
  const navigate = useNavigate();
  const authHttpFormData = useAuthHttp('multipart/form-data');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingWarehouse, setIsFetchingWarehouse] = useState(false);
  const [warehouse, setWarehouse] = useState<Warehouse>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();
  const { isWarehouseManager } = UserRoleService();

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Warehouses', href: '/warehouse-master', current: false },
    {
      name: 'Update Warehouse',
      href: `/warehouse/update/${id}`,
      current: true,
    },
  ];

  const fetchWarehouse = async () => {
    setIsFetchingWarehouse(true);
    try {
      const { data }: AxiosResponse<WarehouseData> = await authHttp.get(
        isWarehouseManager(user)
          ? WAREHOUSE_GET
          : `${WAREHOUSE_SEARCH_GET}/${id}`,
        {
          params: {
            warehouseId: id,
          },
        }
      );
      setWarehouse(data.warehouse);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsFetchingWarehouse(false);
    }
  };

  const handleOnSubmit = async (data: WarehouseFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await authHttpFormData.post(
        WAREHOUSE_UPDATE,
        {
          warehouse: {
            warehouseId: warehouse?.warehouseId,
            name: data.name,
            description: data.description,
            address: data.address,
            postcode: data.postcode || '',
            businessContactEmail: data.businessContactEmail,
            city: data.city,
            country: data.country,
            location: data.location,
            temperatureControlTypes: data.temperatureControl.value,
            temperatureMonitoringCapeabilities: data.temperatureMonitoring.map(
              (temp) => temp.value
            ),
            loadingDocks: data.loadingBays,
            isOffDock: data.facilities.value === 'offDock',
            isQuay: data.facilities.value === 'quay',
            hasOnsiteOffice: data.hasOnsiteOffice,
            onsiteOfficePhoneNumber: {
              countryCode: COUNTRY_CODES.find(
                (country) => country.country_code === data.siteOfficeCountryCode
              )?.country_code,
              countryNumberPrefix: COUNTRY_CODES.find(
                (country) => country.country_code === data.siteOfficeCountryCode
              )?.phone_code.toString(),
              number: data.siteOfficePhoneNumber,
            },
            maxStorageCapacity: data.maxStorageCapacity,
            maxStorageCapacityAvailable: data.maxFLOXCapacity,
            currentlyAvailableCapacity: data.remainingCapacity,
            minStorageCapacityThreshold: data.minCapacityRequirement,
            minStorageTimeThreshold: data.minTerm,
            storageTimeUnitOfMeasure: data.termsUnitOfMeasure.value,
            storageCapacityUnitOfMeasure: data.capacityUnitOfMeasure.value,
            currency: data.currency.value,
            palletReceiptAndPutawayPrice: data.receiptAndPutaway,
            palletPickingAndDespatchPrice: data.palletPickingAndDispatch,
            palletLayerPickingAndDespatchPrice: data.layerPickingAndDispatch,
            palletStoragePricePerWeek: data.storage,
            casePickingAndDespatchPrice: data.casePickingAndDispatch,
            subsequentCasePickingAndDespatchPrice:
              data.subsequentCasePickingAndDispatch,
            itemPickingAndDespatchPrice: data.itemPickingAndDispatch,
            subsequentItemPickingAndDespatchPrice:
              data.subsequentItemPickingAndDispatch,
            containerHandlingPrice: data.containerHandling,
            squareFeetStoragePricePerWeek: data.storage,
            squareMetreStoragePricePerWeek: data.storage,
            demurragePricePerDay: data.demurrageCharges,
            palletReturnsPrice: data.palletReturns,
            itemReturnsPrice: data.itemReturns,
            caseReturnsPrice: data.caseReturns,
            provisionOfPalletPrice: data.provisionOfPallet,
            auxiliaryServicePricePerManHour: data.auxiliaryServices,
            additionalSurchargesFlatFee: data.surcharges,
            extraInfo: data.extraInfo || null,
            numberOfShifts: data.shifts,
            minHeadcount: data.minimumHeadcount,
            maxHeadcount: data.maximumHeadcount,
            standardBusinessHoursType: data.schedule.value,
            hasFlexibleBusinessHours: data.flexibleHoursOnDemand,
            customBusinessHours:
              data.schedule.value === 'Other'
                ? [
                    {
                      day: 'Monday',
                      open: !data.mondayWorkingHours.working
                        ? '00:00:00'
                        : data.mondayWorkingHours.openingTime,
                      close: !data.mondayWorkingHours.working
                        ? '00:00:00'
                        : data.mondayWorkingHours.closingTime,
                    },
                    {
                      day: 'Tuesday',
                      open: !data.tuesdayWorkingHours.working
                        ? '00:00:00'
                        : data.tuesdayWorkingHours.openingTime,
                      close: !data.tuesdayWorkingHours.working
                        ? '00:00:00'
                        : data.tuesdayWorkingHours.closingTime,
                    },
                    {
                      day: 'Wednesday',
                      open: !data.wednesdayWorkingHours.working
                        ? '00:00:00'
                        : data.wednesdayWorkingHours.openingTime,
                      close: !data.wednesdayWorkingHours.working
                        ? '00:00:00'
                        : data.wednesdayWorkingHours.closingTime,
                    },
                    {
                      day: 'Thursday',
                      open: !data.thursdayWorkingHours.working
                        ? '00:00:00'
                        : data.thursdayWorkingHours.openingTime,
                      close: !data.thursdayWorkingHours.working
                        ? '00:00:00'
                        : data.thursdayWorkingHours.closingTime,
                    },
                    {
                      day: 'Friday',
                      open: !data.fridayWorkingHours.working
                        ? '00:00:00'
                        : data.fridayWorkingHours.openingTime,
                      close: !data.fridayWorkingHours.working
                        ? '00:00:00'
                        : data.fridayWorkingHours.closingTime,
                    },
                    {
                      day: 'Saturday',
                      open: !data.saturdayWorkingHours.working
                        ? '00:00:00'
                        : data.saturdayWorkingHours.openingTime,
                      close: !data.saturdayWorkingHours.working
                        ? '00:00:00'
                        : data.saturdayWorkingHours.closingTime,
                    },
                    {
                      day: 'Sunday',
                      open: !data.sundayWorkingHours.working
                        ? '00:00:00'
                        : data.sundayWorkingHours.openingTime,
                      close: !data.sundayWorkingHours.working
                        ? '00:00:00'
                        : data.sundayWorkingHours.closingTime,
                    },
                  ]
                : [],
            vehicleLoadingTimeWindows: [
              {
                day: 'Monday',
                open: !data.mondayLoadingHours.operating
                  ? '00:00:00'
                  : data.mondayLoadingHours.openingTime,
                close: !data.mondayLoadingHours.operating
                  ? '00:00:00'
                  : data.mondayLoadingHours.closingTime,
              },
              {
                day: 'Tuesday',
                open: !data.tuesdayLoadingHours.operating
                  ? '00:00:00'
                  : data.tuesdayLoadingHours.openingTime,
                close: !data.tuesdayLoadingHours.operating
                  ? '00:00:00'
                  : data.tuesdayLoadingHours.closingTime,
              },
              {
                day: 'Wednesday',
                open: !data.wednesdayLoadingHours.operating
                  ? '00:00:00'
                  : data.wednesdayLoadingHours.openingTime,
                close: !data.wednesdayLoadingHours.operating
                  ? '00:00:00'
                  : data.wednesdayLoadingHours.closingTime,
              },
              {
                day: 'Thursday',
                open: !data.thursdayLoadingHours.operating
                  ? '00:00:00'
                  : data.thursdayLoadingHours.openingTime,
                close: !data.thursdayLoadingHours.operating
                  ? '00:00:00'
                  : data.thursdayLoadingHours.closingTime,
              },
              {
                day: 'Friday',
                open: !data.fridayLoadingHours.operating
                  ? '00:00:00'
                  : data.fridayLoadingHours.openingTime,
                close: !data.fridayLoadingHours.operating
                  ? '00:00:00'
                  : data.fridayLoadingHours.closingTime,
              },
              {
                day: 'Saturday',
                open: !data.saturdayLoadingHours.operating
                  ? '00:00:00'
                  : data.saturdayLoadingHours.openingTime,
                close: !data.saturdayLoadingHours.operating
                  ? '00:00:00'
                  : data.saturdayLoadingHours.closingTime,
              },
              {
                day: 'Sunday',
                open: !data.sundayLoadingHours.operating
                  ? '00:00:00'
                  : data.sundayLoadingHours.openingTime,
                close: !data.sundayLoadingHours.operating
                  ? '00:00:00'
                  : data.sundayLoadingHours.closingTime,
              },
            ],
            dispatchCutoffTime: data.cutOffTime,
            minEavesHeight: {
              quantity: data.minEavesHeight,
              unitOfMeasure: data.minEavesHeightUnitOfMeasure.value,
            },
            constructionYear: data.constructionYear,
            grossInternalArea: {
              quantity: data.grossInternalArea,
              unitOfMeasure: data.grossInternalAreaUnitOfMeasure.value,
            },
            mezzanineArea: {
              quantity: data.mezzanineArea,
              unitOfMeasure: data.mezzanineAreaUnitOfMeasure.value,
            },
            isRackingAvailable: data.rackingAvailable,
            individualRackDepth: data.individualRackDepth,
            individualRackHeight: data.individualRackHeight,
            individualRackWidth: data.individualRackWidth,
            totalRackHeight: data.totalRackHeight,
            storageCapabilities: data.specialFacilities.map(
              (facility) => facility.value
            ),
            materialHandlingEquipment: data.materialHandlingEquipment.map(
              (equipment) => equipment.value
            ),
            yardSpace: {
              quantity: data.yardSpace,
              unitOfMeasure: data.yardSpaceUnitOfMeasure.value,
            },
            vehicleAccessHeight: {
              quantity: data.vehicleAccessHeight,
              unitOfMeasure: data.vehicleAccessHeightUnitOfMeasure.value,
            },
            leveledLoadingDocks: data.levelledLoadingDocks,
            doubleHeightLoadingDocks: data.doubleHeightDocks,
            carParkingSpaces: data.carParkingSpaces,
            lorryParkingSpaces: data.lorryParkingSpaces,
            sectorExperience: data.experienceSectors.map(
              (sector) => sector.value
            ),
            warehouseOperations: data.warehouseOperations.map(
              (operation) => operation.value
            ),
            fulfilmentServices: data.fulfilmentServices.map(
              (service) => service.value
            ),
            fleet: [
              {
                type: 'MultiAxleArticMoreThan26t',
                count: data.articsOver26t,
              },
              {
                type: 'ThreeAxleRigidLessThan26t',
                count: data.rigidsUnder26t,
              },
              { type: 'TwoAxleRigid18t', count: data.rigidsUnder18t },
              {
                type: 'TwoAxleRigidLessThan7AndHalf5t',
                count: data.rigidsUnder7_5t,
              },
              {
                type: 'LgvLessThan3AndHalft',
                count: data.ldvsUnder3_5t,
              },
              {
                type: 'VanLessThan1AndHalft',
                count: data.vansUnder1_5t,
              },
              { type: 'SpecialOrOther', count: data.specialOther },
            ],
            additionalServices: data.additionalServices.map(
              (service) => service.value
            ),
            safetyAndSecurity: data.safetyAndSecurity.map((item) => item.value),
            systemsAvailable: data.systems.map((system) => system.value),
            accreditationsAndMemberships: data.accreditations.includes({
              label: 'None',
              value: 'none',
            })
              ? []
              : data.accreditations.map((accreditation) => accreditation.value),
            accreditationOther: data.accreditationOther || null,
          },
          images: data.warehouseImages.map((image) => ({
            id: crypto.randomUUID(),
            title: image.name,
            image,
          })),
          deletedImageIds: data.deletedImagesIds,
        },
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      navigate(`/warehouse/${id}`);
    } catch (error: any) {
      const message =
        (error?.response?.status === 413 &&
          'Your upload is too large. Please use a smaller image and try again.') ||
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        'Something went wrong. Please check your details and try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchWarehouse();
  }, []);

  if (isFetchingWarehouse) {
    return (
      <div className='flex flex-row justify-center items-center mt-12'>
        <FloxSpinner size='lg' />
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div className='flex flex-row justify-center items-center mt-12'>
        <span className='text-gray-700 text-lg'>No warehouse found</span>
      </div>
    );
  }

  const getWorkingHoursForDay = (
    day: string,
    customBusinessHours: Array<{ day: string; open?: string; close?: string }>
  ) => {
    const hours = customBusinessHours.find((d) => d.day === day);
    return {
      working: Boolean(
        hours?.open !== '00:00:00' && hours?.close !== '00:00:00'
      ),
      openingTime: hours?.open?.slice(0, 5) || '06:00',
      closingTime: hours?.close?.slice(0, 5) || '18:00',
    };
  };

  const getLoadingHoursForDay = (
    day: string,
    vehicleLoadingTimeWindows: Array<{
      day: string;
      open?: string;
      close?: string;
    }>
  ) => {
    const hours = vehicleLoadingTimeWindows.find((d) => d.day === day);
    return {
      operating: Boolean(
        hours?.open !== '00:00:00' && hours?.close !== '00:00:00'
      ),
      openingTime: hours?.open?.slice(0, 5) || '06:00',
      closingTime: hours?.close?.slice(0, 5) || '18:00',
    };
  };

  const getOptionalNumberString = (value?: number): string => {
    return value === 0 || value === undefined || value === null
      ? ''
      : Number(value).toString();
  };

  const getFleetCountString = (
    fleet: Array<{ type: string; count: number }>,
    type: string
  ): string => {
    const vehicle = fleet.find((v) => v.type === type);
    return vehicle &&
      vehicle.count !== undefined &&
      vehicle.count !== null &&
      vehicle.count > 0
      ? vehicle.count.toString()
      : '';
  };

  const processWarehouseToInitialValues = (): WarehouseFormValues => {
    const initialValues: WarehouseFormValues = {
      ...warehouse,
      postcode: (warehouse as any).postcode || '',
      businessContactEmail:
        warehouse.businessContactEmail || businessProfile?.contactEmail || '',
      constructionYear: getOptionalNumberString(warehouse.constructionYear),
      experienceSectors: warehouse.sectorExperience.map((sector) => ({
        label: sector,
        value: sector,
      })),
      accreditations: warehouse.accreditationsAndMemberships.map(
        (accreditation) => ({
          label: accreditation,
          value: accreditation,
        })
      ),
      accreditationOther: warehouse.accreditationOther ?? '',
      temperatureControl: {
        label: warehouse.temperatureControlTypes[0],
        value: warehouse.temperatureControlTypes[0],
      },
      temperatureMonitoring: warehouse.temperatureMonitoringCapeabilities.map(
        (temp) => ({
          label: temp,
          value: temp,
        })
      ),
      systems: warehouse.systemsAvailable.map((system) => ({
        label: system,
        value: system,
      })),
      safetyAndSecurity: warehouse.safetyAndSecurity.map((item) => ({
        label: item,
        value: item,
      })),
      capacityUnitOfMeasure: CAPACITY_UOM.find(
        (option) => option.value === warehouse.storageCapacityUnitOfMeasure
      ) || {
        label: warehouse.storageCapacityUnitOfMeasure,
        value: warehouse.storageCapacityUnitOfMeasure,
      },
      maxStorageCapacity: warehouse.maxStorageCapacity.toString(),
      maxFLOXCapacity: warehouse.maxStorageCapacityAvailable.toString(),
      remainingCapacity: warehouse.currentlyAvailableCapacity.toString(),
      minCapacityRequirement: warehouse.minStorageCapacityThreshold.toString(),
      minTerm: warehouse.minStorageTimeThreshold.toString(),
      termsUnitOfMeasure: SERVICE_TIME_UNITS.find(
        (option) => option.value === warehouse.storageTimeUnitOfMeasure
      ) || {
        label: warehouse.storageTimeUnitOfMeasure,
        value: warehouse.storageTimeUnitOfMeasure,
      },
      facilities: warehouse.isOffDock
        ? { label: 'Off Dock', value: 'offDock' }
        : warehouse.isQuay
        ? { label: 'Quay', value: 'quay' }
        : { label: 'On Dock', value: 'onDock' },
      loadingBays: getOptionalNumberString(warehouse.loadingDocks),
      siteOfficeCountryCode:
        COUNTRY_CODES.find(
          (country) =>
            country.country_code ===
            warehouse.onsiteOfficePhoneNumber.countryCode
        )?.country_code || '',
      siteOfficePhoneNumber: warehouse.onsiteOfficePhoneNumber.number || '',
      specialFacilities: warehouse.storageCapabilities.map((facility) => ({
        label: facility,
        value: facility,
      })),
      materialHandlingEquipment: warehouse.materialHandlingEquipment.map(
        (equipment) => ({
          label: equipment,
          value: equipment,
        })
      ),
      lorryParkingSpaces: getOptionalNumberString(warehouse.lorryParkingSpaces),
      carParkingSpaces: getOptionalNumberString(warehouse.carParkingSpaces),
      rackingAvailable: warehouse.isRackingAvailable,
      individualRackDepth: getOptionalNumberString(
        warehouse.individualRackDepth
      ),
      individualRackHeight: getOptionalNumberString(
        warehouse.individualRackHeight
      ),
      individualRackWidth: getOptionalNumberString(
        warehouse.individualRackWidth
      ),
      totalRackHeight: getOptionalNumberString(warehouse.totalRackHeight),
      warehouseOperations: warehouse.warehouseOperations.map((operation) => ({
        label: operation,
        value: operation,
      })),
      fulfilmentServices: warehouse.fulfilmentServices.map((service) => ({
        label: service,
        value: service,
      })),
      additionalServices: warehouse.additionalServices.map((service) => ({
        label: service,
        value: service,
      })),
      currency: CURRENCY.find(
        (option) => option.value === warehouse.currency
      ) || {
        label: warehouse.currency,
        value: warehouse.currency,
      },
      storage: warehouse.palletStoragePricePerWeek?.toString(),
      schedule: STANDARD_BUSINESS_HOURS.find(
        (option) => option.value === warehouse.standardBusinessHoursType
      ) || {
        label: warehouse.standardBusinessHoursType,
        value: warehouse.standardBusinessHoursType,
      },
      shifts: getOptionalNumberString(warehouse.numberOfShifts),
      minimumHeadcount: getOptionalNumberString(warehouse.minHeadcount),
      maximumHeadcount: getOptionalNumberString(warehouse.maxHeadcount),
      flexibleHoursOnDemand: warehouse.hasFlexibleBusinessHours,
      cutOffTime: warehouse.dispatchCutoffTime || '18:00',
      country: '',
      images: warehouse.images,
      warehouseImages: [],
      deletedImagesIds: [],
      grossInternalArea: getOptionalNumberString(
        warehouse.grossInternalArea?.quantity
      ),
      grossInternalAreaUnitOfMeasure: {
        label:
          SQUARE_ACCESS_SPACE_UOM.find(
            (option) =>
              option.value === warehouse.grossInternalArea?.unitOfMeasure
          )?.label || warehouse.grossInternalArea?.unitOfMeasure,
        value: warehouse.grossInternalArea?.unitOfMeasure,
      },
      yardSpace: getOptionalNumberString(warehouse.yardSpace?.quantity),
      yardSpaceUnitOfMeasure: {
        label:
          SQUARE_ACCESS_SPACE_UOM.find(
            (option) => option.value === warehouse.yardSpace?.unitOfMeasure
          )?.label || warehouse.yardSpace?.unitOfMeasure,
        value: warehouse.yardSpace?.unitOfMeasure,
      },
      levelledLoadingDocks: getOptionalNumberString(
        warehouse.leveledLoadingDocks
      ),
      minEavesHeight: getOptionalNumberString(
        warehouse.minEavesHeight?.quantity
      ),
      minEavesHeightUnitOfMeasure: {
        label:
          ACCESS_SPACE_UOM.find(
            (option) => option.value === warehouse.minEavesHeight?.unitOfMeasure
          )?.label || warehouse.minEavesHeight?.unitOfMeasure,
        value: warehouse.minEavesHeight?.unitOfMeasure,
      },
      mezzanineArea: getOptionalNumberString(warehouse.mezzanineArea?.quantity),
      mezzanineAreaUnitOfMeasure: {
        label:
          ACCESS_SPACE_UOM.find(
            (option) => option.value === warehouse.mezzanineArea?.unitOfMeasure
          )?.label || warehouse.mezzanineArea?.unitOfMeasure,
        value: warehouse.mezzanineArea?.unitOfMeasure,
      },
      vehicleAccessHeight: getOptionalNumberString(
        warehouse.vehicleAccessHeight?.quantity
      ),
      vehicleAccessHeightUnitOfMeasure: {
        label:
          ACCESS_SPACE_UOM.find(
            (option) =>
              option.value === warehouse.vehicleAccessHeight?.unitOfMeasure
          )?.label || warehouse.vehicleAccessHeight?.unitOfMeasure,
        value: warehouse.vehicleAccessHeight?.unitOfMeasure,
      },
      doubleHeightDocks: getOptionalNumberString(
        warehouse.doubleHeightLoadingDocks
      ),
      receiptAndPutaway: getOptionalNumberString(
        warehouse.palletReceiptAndPutAwayPrice
      ),
      palletPickingAndDispatch: getOptionalNumberString(
        warehouse.palletPickingAndDespatchPrice
      ),
      layerPickingAndDispatch: getOptionalNumberString(
        warehouse.palletLayerPickingAndDespatchPrice
      ),
      casePickingAndDispatch: getOptionalNumberString(
        warehouse.casePickingAndDespatchPrice
      ),
      subsequentCasePickingAndDispatch: getOptionalNumberString(
        warehouse.subsequentCasePickingAndDespatchPrice
      ),
      itemPickingAndDispatch: getOptionalNumberString(
        warehouse.itemPickingAndDespatchPrice
      ),
      subsequentItemPickingAndDispatch: getOptionalNumberString(
        warehouse.subsequentItemPickingAndDespatchPrice
      ),
      auxiliaryServices: getOptionalNumberString(
        warehouse.auxiliaryServicePricePerManHour
      ),
      demurrageCharges: getOptionalNumberString(warehouse.demurragePricePerDay),
      containerHandling: getOptionalNumberString(
        warehouse.containerHandlingPrice
      ),
      palletReturns: getOptionalNumberString(warehouse.palletReturnsPrice),
      caseReturns: getOptionalNumberString(warehouse.caseReturnsPrice),
      itemReturns: getOptionalNumberString(warehouse.itemReturnsPrice),
      surcharges: getOptionalNumberString(
        warehouse.additionalSurchargesFlatFee
      ),
      provisionOfPallet: getOptionalNumberString(
        warehouse.provisionOfPalletPrice
      ),
      extraInfo: warehouse.extraInfo ?? '',
      haveOwnFleet: warehouse.fleet.some(
        (vehicle) => Number(vehicle.count) > 0
      ),
      articsOver26t: getFleetCountString(
        warehouse.fleet,
        'MultiAxleArticMoreThan26t'
      ),
      rigidsUnder18t: getFleetCountString(warehouse.fleet, 'TwoAxleRigid18t'),
      ldvsUnder3_5t: getFleetCountString(
        warehouse.fleet,
        'LgvLessThan3AndHalft'
      ),
      vansUnder1_5t: getFleetCountString(
        warehouse.fleet,
        'VanLessThan1AndHalft'
      ),
      rigidsUnder26t: getFleetCountString(
        warehouse.fleet,
        'ThreeAxleRigidLessThan26t'
      ),
      rigidsUnder7_5t: getFleetCountString(
        warehouse.fleet,
        'TwoAxleRigidLessThan7AndHalf5t'
      ),
      specialOther: getFleetCountString(warehouse.fleet, 'SpecialOrOther'),
      mondayWorkingHours: getWorkingHoursForDay(
        'Monday',
        warehouse.customBusinessHours
      ),
      tuesdayWorkingHours: getWorkingHoursForDay(
        'Tuesday',
        warehouse.customBusinessHours
      ),
      wednesdayWorkingHours: getWorkingHoursForDay(
        'Wednesday',
        warehouse.customBusinessHours
      ),
      thursdayWorkingHours: getWorkingHoursForDay(
        'Thursday',
        warehouse.customBusinessHours
      ),
      fridayWorkingHours: getWorkingHoursForDay(
        'Friday',
        warehouse.customBusinessHours
      ),
      saturdayWorkingHours: getWorkingHoursForDay(
        'Saturday',
        warehouse.customBusinessHours
      ),
      sundayWorkingHours: getWorkingHoursForDay(
        'Sunday',
        warehouse.customBusinessHours
      ),
      mondayLoadingHours: getLoadingHoursForDay(
        'Monday',
        warehouse.vehicleLoadingTimeWindows
      ),
      tuesdayLoadingHours: getLoadingHoursForDay(
        'Tuesday',
        warehouse.vehicleLoadingTimeWindows
      ),
      wednesdayLoadingHours: getLoadingHoursForDay(
        'Wednesday',
        warehouse.vehicleLoadingTimeWindows
      ),
      thursdayLoadingHours: getLoadingHoursForDay(
        'Thursday',
        warehouse.vehicleLoadingTimeWindows
      ),
      fridayLoadingHours: getLoadingHoursForDay(
        'Friday',
        warehouse.vehicleLoadingTimeWindows
      ),
      saturdayLoadingHours: getLoadingHoursForDay(
        'Saturday',
        warehouse.vehicleLoadingTimeWindows
      ),
      sundayLoadingHours: getLoadingHoursForDay(
        'Sunday',
        warehouse.vehicleLoadingTimeWindows
      ),
    };

    return initialValues;
  };

  return (
    <WarehouseForm
      onSubmit={handleOnSubmit}
      isSubmitting={isSubmitting}
      header='Update Warehouse'
      pages={pages}
      initialValues={processWarehouseToInitialValues()}
      submitError={submitError}
      onDismissError={() => setSubmitError(null)}
    />
  );
}
